import { Link } from "react-router-dom";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { QueueTable, Td } from "../../features/web3/forex/components/table";
import { formatDateTime } from "../../lib/format";
import { useCan, useForexAdmin } from "./api";
import {
  BASE,
  ConnectionLink,
  DealCard,
  Drawer,
  ListView,
  Money,
  PeriodLink,
  QueryState,
  Stat,
  UserLink,
  linkCls,
  mono,
  muted,
  orDash,
  useOpen,
  useTab,
} from "./parts";
import {
  ACCESS_FEE_COLUMNS,
  CHARGE_STATE,
  ReleaseRetry,
  NO_SPONSOR_REASON,
  SponsorLink,
  accessFeeRow,
  chargeColumns,
  chargeRow,
  failureLabel,
  paidToLabel,
} from "./rows";

const TABS = [
  { value: "periods", label: "Periods" },
  { value: "settlements", label: "Settlements" },
  { value: "charges", label: "Charges" },
  { value: "access-fees", label: "Access fees" },
  { value: "earnings", label: "Earnings" },
  { value: "sponsors", label: "Sponsors" },
];
// Reports, so they need reports.read on top of the page's own (025).
const REPORT_TABS = ["earnings", "sponsors"];

const MasterCell = ({ m }) => (
  <Link to={`${BASE}/masters/${m.id}`} className={linkCls}>
    {m.name ?? m.username}
  </Link>
);

const RANGE = [
  { name: "from", label: "From", type: "datetime-local" },
  { name: "to", label: "To", type: "datetime-local" },
];

// ── Periods ──
const PERIOD_COLUMNS = [
  { label: "Master" }, { label: "Owner" }, { label: "Login" }, { label: "Starts" }, { label: "Ends" },
  { label: "Cut", align: "right" }, { label: "Schedule" }, { label: "Status" }, { label: "Closed" },
  { label: "Recorded" }, { label: "Settlements", align: "right" },
  { label: "Paid", align: "right" }, { label: "Carried", align: "right" }, { label: "Earned", align: "right" },
  { label: "Sponsor share", align: "right" },
];

const periodRow = (r) => (
  <tr key={r.id} className="hover:bg-white/[0.02]">
    <Td className="whitespace-nowrap"><MasterCell m={r.master} /></Td>
    <Td className={mono}><UserLink username={r.master.username} /></Td>
    {/* null once the master's account was removed. */}
    <Td className={mono}>{orDash(r.master.login)}</Td>
    <Td className={muted}>{formatDateTime(r.startsAt)}</Td>
    <Td className={muted}>{formatDateTime(r.endsAt)}</Td>
    <Td align="right" className={mono}>{r.cutPercent}%</Td>
    <Td className="whitespace-nowrap text-xs">{r.schedule.replaceAll("_", " ")}</Td>
    <Td className="text-xs">
      {!r.closedAt ? <span className="text-emerald-400">Running</span> : r.recordedAt ? "Closed" : <span className="text-amber-400">Recording…</span>}
    </Td>
    <Td className={muted}>{r.closedAt ? formatDateTime(r.closedAt) : orDash(null)}</Td>
    <Td className={muted}>{r.recordedAt ? formatDateTime(r.recordedAt) : orDash(null)}</Td>
    <Td align="right" className={mono}>
      <Link to={`${BASE}/settlements?tab=settlements&period=${r.id}`} className={linkCls}>{r.settlements}</Link>
    </Td>
    {/* A closed period with settlements above paid still has copiers owing. */}
    <Td align="right" className={`${mono} ${r.closedAt && r.paidSettlements < r.settlements ? "text-amber-400" : ""}`}>{r.paidSettlements}</Td>
    <Td align="right" className={mono}>{r.carriedSettlements}</Td>
    {/* v1.25.0: earned is what the master kept; the sponsor's part is beside it. */}
    <Td align="right"><Money value={r.earned} /></Td>
    <Td align="right"><Money value={r.sponsorShare} /></Td>
  </tr>
);

// ── Settlements ──
export const SETTLEMENT_STATUS = {
  UNPAID: "text-amber-400",
  CARRIED: "text-orange-400",
  // v1.20.0 (034): a loss left for later profit to make back. Never owing.
  UNRECOVERED_LOSS: "text-sky-400",
  CHARGING: "text-red-400",
  // v1.28.0 (042): paid by the copier; the master is paid at the charge's releaseAt.
  AWAITING_RELEASE: "text-cyan-400",
  PAID: "text-emerald-400",
};

// 016's labels.
const SETTLEMENT_LABEL = {
  UNPAID: "Unpaid",
  CARRIED: "Carried",
  UNRECOVERED_LOSS: "Unrecovered loss",
  CHARGING: "Charging",
  AWAITING_RELEASE: "Awaiting release",
  PAID: "Paid",
};
const settlementLabel = (s) => SETTLEMENT_LABEL[s] ?? s;

const SETTLEMENT_COLUMNS = [
  { label: "Status" }, { label: "Copier" }, { label: "Master" }, { label: "Master owner" },
  { label: "Master login" }, { label: "Window starts" }, { label: "Window ends" },
  { label: "Profit", align: "right" }, { label: "Cut", align: "right" }, { label: "Carried" },
  { label: "Paid" }, { label: "Charge" }, { label: "Connection" }, { label: "Period" }, { label: "Created" },
];

const settlementRow = (onCharge) => (r) => (
  <tr key={r.id} className="hover:bg-white/[0.02]">
    <Td className={`text-xs font-medium ${SETTLEMENT_STATUS[r.status]}`}>{settlementLabel(r.status)}</Td>
    <Td className={mono}><UserLink username={r.copier.username} /> · {orDash(r.copier.login)}</Td>
    <Td className="whitespace-nowrap"><MasterCell m={r.master} /></Td>
    <Td className={mono}><UserLink username={r.master.username} /></Td>
    <Td className={mono}>{orDash(r.master.login)}</Td>
    <Td className={muted}>{formatDateTime(r.startsAt)}</Td>
    <Td className={muted}>{formatDateTime(r.endsAt)}</Td>
    <Td align="right"><Money value={r.profit} /></Td>
    <Td align="right" className={mono}>{r.cutPercent}%</Td>
    <Td className={muted}>{r.carriedAt ? formatDateTime(r.carriedAt) : orDash(null)}</Td>
    <Td className={muted}>{r.paidAt ? formatDateTime(r.paidAt) : orDash(null)}</Td>
    {/* PAID with no charge = netted flat or down, paid at zero. */}
    <Td className="text-xs">
      {r.chargeId ? (
        <button type="button" onClick={() => onCharge(r.chargeId)} className={linkCls}>Open →</button>
      ) : r.status === "PAID" ? "Zero share" : orDash(null)}
    </Td>
    <Td className={mono}><ConnectionLink id={r.connectionId} /></Td>
    <Td className={mono}><PeriodLink id={r.periodId} /></Td>
    <Td className={muted}>{formatDateTime(r.createdAt)}</Td>
  </tr>
);

const PARTY_FIELDS = [
  { name: "copierUsername", label: "Copier username" },
  { name: "copierLogin", label: "Copier login" },
  { name: "masterUsername", label: "Master username" },
  { name: "masterLogin", label: "Master login" },
];
const PARTY_EMPTY = { copierUsername: "", copierLogin: "", masterUsername: "", masterLogin: "" };

// ── Earnings ──
const EARNING_COLUMNS = [
  { label: "Master" }, { label: "Owner" }, { label: "Login" },
  { label: "Earned", align: "right" }, { label: "Sponsor share", align: "right" },
  // v1.28.0 (042): charged from the copier, not released into the wallets yet.
  { label: "Awaiting release", align: "right" }, { label: "Sponsor share awaiting release", align: "right" },
  { label: "Charging", align: "right" }, { label: "Sponsor share charging", align: "right" },
  { label: "Charges", align: "right" }, { label: "Access fees", align: "right" },
];

const earningRow = (r) => (
  <tr key={r.master.id} className="hover:bg-white/[0.02]">
    <Td className="whitespace-nowrap"><MasterCell m={r.master} /></Td>
    <Td className={mono}><UserLink username={r.master.username} /></Td>
    {/* v1.24.0: null when every charge in the range came after its account was removed. */}
    <Td className={mono}>{orDash(r.master.login)}</Td>
    <Td align="right"><Money value={r.earned} /></Td>
    <Td align="right"><Money value={r.sponsorShare} /></Td>
    <Td align="right"><Money value={r.awaitingRelease} /></Td>
    <Td align="right"><Money value={r.sponsorShareAwaitingRelease} /></Td>
    <Td align="right"><Money value={r.charging} /></Td>
    <Td align="right"><Money value={r.sponsorShareCharging} /></Td>
    <Td align="right" className={mono}>{r.charges}</Td>
    {/* v1.17.0: DONE access fees in the range, never part of earned. */}
    <Td align="right"><Money value={r.accessFees} /></Td>
  </tr>
);

// ── Sponsors (v1.25.0) ──
const SPONSOR_COLUMNS = [
  { label: "Sponsor" }, { label: "Sponsor share", align: "right" }, { label: "Awaiting release", align: "right" },
  { label: "Charging", align: "right" },
  { label: "Charges", align: "right" }, { label: "From masters" },
];

const sponsorRow = (r) => (
  <tr key={r.sponsorUsername} className="hover:bg-white/[0.02]">
    <Td className={mono}><SponsorLink username={r.sponsorUsername} /></Td>
    <Td align="right"><Money value={r.sponsorShare} /></Td>
    <Td align="right"><Money value={r.sponsorShareAwaitingRelease} /></Td>
    <Td align="right"><Money value={r.sponsorShareCharging} /></Td>
    <Td align="right" className={mono}>{r.charges}</Td>
    <Td className="text-xs">
      {r.masters.map((m) => (
        <span key={m.id} className="block whitespace-nowrap">
          <MasterCell m={m} /> · <Money value={m.sponsorShare} />
          {Number(m.sponsorShareAwaitingRelease) > 0 && <> · awaiting <Money value={m.sponsorShareAwaitingRelease} /></>}
          {Number(m.sponsorShareCharging) > 0 && <> · charging <Money value={m.sponsorShareCharging} /></>}
          {` · ${m.charges} charge${m.charges === 1 ? "" : "s"}`}
        </span>
      ))}
    </Td>
  </tr>
);

// ── Charge drawer ──
const CHARGE_SETTLEMENT_COLUMNS = [
  { label: "Status" }, { label: "Window" }, { label: "Profit", align: "right" }, { label: "Cut", align: "right" },
  { label: "Carried" }, { label: "Paid" }, { label: "Period" }, { label: "Created" },
];

// Also opened from connection and user pages, so it checks settlements.read itself (025).
export function ChargeDrawer({ id, onClose }) {
  if (!useCan()("settlements.read")) id = null;
  const q = useForexAdmin(id ? `/settlements/admin/charges/${id}` : null);
  return (
    <Drawer open={id} onClose={onClose} title="Charge">
      <QueryState q={q}>
        {(c) => {
          const [label, tone] = CHARGE_STATE[c.state] ?? [c.state, "text-muted-foreground"];
          return (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Stat label="State"><span className={tone}>{label}</span><span className="text-xs"><ReleaseRetry c={c} /></span></Stat>
                <Stat label="Share"><Money value={c.share} /></Stat>
                <Stat label="Profit netted"><Money value={c.profit} /></Stat>
                <Stat label="Cut">{c.cutPercent}%</Stat>
                {/* The copier's refund after the master's credit was refused. The detail
                    does not return its deal, only the ticket. */}
                <Stat label="Debit ticket">{orDash(c.debitTicket)}</Stat>
                <Stat label="Credit ticket">{orDash(c.creditTicket)}</Stat>
                <Stat label="Refund ticket">{orDash(c.refundTicket)}</Stat>
                {/* v1.28.0 (042): when the share is paid into the wallets. */}
                <Stat label="Release at"><span className="text-sm">{c.releaseAt ? formatDateTime(c.releaseAt) : orDash(null)}</span></Stat>
                <Stat label="Released"><span className="text-sm">{c.releasedAt ? formatDateTime(c.releasedAt) : orDash(null)}</span></Stat>
                <Stat label="Period"><span className="text-sm"><PeriodLink id={c.periodId} /></span></Stat>
                {/* v1.25.0: share = master part + sponsor share. */}
                <Stat label="Master part"><Money value={c.masterPart} /></Stat>
                <Stat label="Sponsor"><span className="text-sm"><SponsorLink username={c.sponsorUsername} /></span></Stat>
                <Stat label="Sponsor percent">{c.sponsorPercent}%</Stat>
                <Stat label="Sponsor share"><Money value={c.sponsorShare} /></Stat>
                {c.noSponsorShareReason && (
                  <Stat label="No sponsor share">
                    <span className="text-sm">{NO_SPONSOR_REASON[c.noSponsorShareReason] ?? c.noSponsorShareReason}</span>
                  </Stat>
                )}
                {c.sponsorWalletTranxId && (
                  <Stat label="Sponsor wallet transaction"><span className="break-all font-mono text-xs">{c.sponsorWalletTranxId}</span></Stat>
                )}
                <Stat label="Paid to">{paidToLabel(c.paidTo)}</Stat>
                {/* The wallet's own id, never an MT5 ticket. */}
                {c.paidTo === "WALLET" && (
                  <Stat label="Wallet transaction"><span className="break-all font-mono text-xs">{orDash(c.walletTranxId)}</span></Stat>
                )}
                <Stat label="Copier"><span className="text-sm"><UserLink username={c.copier.username} /> · {orDash(c.copier.login)}</span></Stat>
                <Stat label="Master">
                  <span className="text-sm"><MasterCell m={c.master} /> · <UserLink username={c.master.username} /> · {orDash(c.master.login)}</span>
                </Stat>
              </div>
              {c.failure && (
                <p className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
                  {failureLabel(c.failure)}
                  {c.failure.status != null && ` · ${c.failure.status}`}
                  {c.failure.message && ` · ${c.failure.message}`}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Ref {c.ref} · id <span className="font-mono">{c.id}</span> · created {formatDateTime(c.createdAt)} ·{" "}
                <Link to={`${BASE}/connections/${c.connectionId}`} className={linkCls}>connection →</Link>
              </p>
              {c.deals ? (
                <div className="space-y-3">
                  <DealCard label="Debit (copier)" deal={c.deals.debit} />
                  {c.paidTo === "WALLET" ? (
                    <p className="text-xs text-muted-foreground">Paid to the FOREX wallet, not on MT5.</p>
                  ) : (
                    <DealCard label="Credit (master)" deal={c.deals.credit} />
                  )}
                </div>
              ) : (
                <p className="text-sm text-amber-400">Deals unavailable right now, try again.</p>
              )}
              <div>
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                  Settlements it pays {c.state === "FAILED" && "(none: a failed charge releases them)"}
                </p>
                <QueueTable
                  columns={CHARGE_SETTLEMENT_COLUMNS}
                  rows={c.settlements}
                  renderRow={(s) => (
                    <tr key={s.id}>
                      <Td className={`text-xs ${SETTLEMENT_STATUS[s.status]}`}>{settlementLabel(s.status)}</Td>
                      <Td className={muted}>{formatDateTime(s.startsAt)} → {formatDateTime(s.endsAt)}</Td>
                      <Td align="right"><Money value={s.profit} /></Td>
                      <Td align="right" className={mono}>{s.cutPercent}%</Td>
                      <Td className={muted}>{s.carriedAt ? formatDateTime(s.carriedAt) : orDash(null)}</Td>
                      <Td className={muted}>{s.paidAt ? formatDateTime(s.paidAt) : orDash(null)}</Td>
                      <Td className={mono}><PeriodLink id={s.periodId} /></Td>
                      <Td className={muted}>{formatDateTime(s.createdAt)}</Td>
                    </tr>
                  )}
                />
              </div>
            </>
          );
        }}
      </QueryState>
    </Drawer>
  );
}

function Tab({ tab, onCharge }) {
  switch (tab) {
    case "settlements":
      return (
        <ListView
          path="/settlements/admin"
          empty={{ status: "", ...PARTY_EMPTY, connection: "", period: "", charge: "" }}
          fields={[
            { name: "status", label: "Status", options: [
              { value: "", label: "All" }, { value: "UNPAID", label: "Unpaid" }, { value: "CARRIED", label: "Carried" },
              { value: "UNRECOVERED_LOSS", label: "Unrecovered loss" },
              { value: "CHARGING", label: "Charging" }, { value: "AWAITING_RELEASE", label: "Awaiting release" },
              { value: "PAID", label: "Paid" },
            ] },
            ...PARTY_FIELDS,
            { name: "connection", label: "Connection id" },
            { name: "period", label: "Period id" },
            { name: "charge", label: "Charge id" },
          ]}
          columns={SETTLEMENT_COLUMNS}
          row={settlementRow(onCharge)}
          label="settlements"
        />
      );
    case "charges":
      return (
        <ListView
          path="/settlements/admin/charges"
          empty={{ state: "", ...PARTY_EMPTY, sponsorUsername: "", connection: "", period: "", from: "", to: "" }}
          fields={[
            { name: "state", label: "State", options: [
              { value: "", label: "All" }, { value: "UNKNOWN", label: "Unknown (needs an operator)" },
              { value: "AWAITING_RELEASE", label: "Awaiting release" },
              { value: "DONE", label: "Done" }, { value: "FAILED", label: "Failed" },
            ] },
            ...PARTY_FIELDS,
            { name: "sponsorUsername", label: "Sponsor username" },
            { name: "connection", label: "Connection id" },
            { name: "period", label: "Period id" },
            ...RANGE,
          ]}
          columns={chargeColumns(false)}
          row={chargeRow(onCharge, false)}
          label="charges"
        />
      );
    case "access-fees":
      return (
        <ListView
          path="/settlements/admin/access-fees"
          empty={{ state: "", paidVia: "", ...PARTY_EMPTY, paidWithNoConnection: "", from: "", to: "" }}
          fields={[
            // v1.26.0 (041): an unknown MT5 fee is settled on the trading server, an
            // unknown wallet fee with the dashboard by its fee-<id>.
            { name: "state", label: "State", options: [
              { value: "", label: "All" }, { value: "UNKNOWN", label: "Unknown (needs an operator)" },
              { value: "DONE", label: "Done" }, { value: "FAILED", label: "Failed" },
            ] },
            { name: "paidVia", label: "Paid via", options: [
              { value: "", label: "All" }, { value: "MT5", label: "MT5" }, { value: "WALLET", label: "FOREX wallet" },
            ] },
            ...PARTY_FIELDS,
            { name: "paidWithNoConnection", label: "Connection", options: [
              { value: "", label: "All" }, { value: "true", label: "Paid, no connection (refund by hand)" },
              { value: "false", label: "Everything else" },
            ] },
            ...RANGE,
          ]}
          columns={ACCESS_FEE_COLUMNS}
          row={accessFeeRow}
          label="access fees"
        />
      );
    case "earnings":
      return (
        <ListView
          path="/reports/admin/earnings"
          empty={{ from: "", to: "" }}
          fields={[
            { name: "from", label: "From (default 30 days ago)", type: "datetime-local" },
            { name: "to", label: "To (default now)", type: "datetime-local" },
          ]}
          columns={EARNING_COLUMNS}
          row={earningRow}
          label="masters"
          head={(d) => (
            <p className="text-xs text-muted-foreground">
              {formatDateTime(d.from)} → {formatDateTime(d.to)}. Charges count by when they were made, so this
              can differ from the sum of a master's period earnings. Earned is what the master kept, after
              its sponsor share.
            </p>
          )}
        />
      );
    case "sponsors":
      return (
        <ListView
          path="/reports/admin/sponsors"
          empty={{ sponsorUsername: "", from: "", to: "" }}
          fields={[
            { name: "sponsorUsername", label: "Sponsor username" },
            { name: "from", label: "From (default 30 days ago)", type: "datetime-local" },
            { name: "to", label: "To (default now)", type: "datetime-local" },
          ]}
          columns={SPONSOR_COLUMNS}
          row={sponsorRow}
          label="sponsors"
          head={(d) => (
            <p className="text-xs text-muted-foreground">
              {formatDateTime(d.from)} → {formatDateTime(d.to)}. Only charges that paid a sponsor share. Charging is
              not known yet: never add it to the share.
            </p>
          )}
        />
      );
    default:
      return (
        <ListView
          path="/settlements/admin/periods"
          empty={{ status: "", username: "", master: "", from: "", to: "" }}
          fields={[
            { name: "status", label: "Status", options: [
              { value: "", label: "All" }, { value: "RUNNING", label: "Running" }, { value: "CLOSED", label: "Closed" },
            ] },
            { name: "username", label: "Master username" },
            { name: "master", label: "Master id" },
            { name: "from", label: "Starts from", type: "datetime-local" },
            { name: "to", label: "Starts to", type: "datetime-local" },
          ]}
          columns={PERIOD_COLUMNS}
          row={periodRow}
          label="periods"
        />
      );
  }
}

export default function Settlements() {
  const can = useCan();
  const tabs = can("reports.read") ? TABS : TABS.filter((t) => !REPORT_TABS.includes(t.value));
  const [picked, setTab] = useTab("periods");
  const tab = tabs.some((t) => t.value === picked) ? picked : "periods";
  const [open, setOpen] = useOpen();
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;

  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Copy trading"
      accent="settlements"
      description="What copiers owe masters, period by period, and the charges that move it. Resolving an unknown charge is still done by hand."
      statusOptions={tabs}
      status={tab}
      onStatus={setTab}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <Tab key={tab} tab={tab} onCharge={setOpen} />
      <ChargeDrawer id={open} onClose={() => setOpen(null)} />
    </QueueLayout>
  );
}
